import { type JSX, useMemo } from 'react'

import { messageVariants } from '~/components/component-library/Message/styles'
import { type MessageProps } from '~/components/component-library/Message/types'
import { CheckCircle } from '~/icons/CheckCircle'
import { Info } from '~/icons/Info'
import { OctagonAlert } from '~/icons/OctagonAlert'
import { TriangleAlert } from '~/icons/TriangleAlert'
import { cn } from '~/utils/tailwind-utils'

export { type MessageProps } from '~/components/component-library/Message/types'

export const Message = ({
  children,
  variant = 'neutral',
  title,
  meta,
  action,
}: MessageProps): JSX.Element => {
  const icon = useMemo(() => {
    if (variant === 'info') return <Info />
    if (variant === 'warning') return <TriangleAlert />
    if (variant === 'success') return <CheckCircle />
    if (variant === 'danger') return <OctagonAlert />
    return null
  }, [variant])

  // A single-line message reads as one row, so the icon, meta, and action
  // center against the text. Titled messages run taller and keep the icon on
  // the first line.
  const isSingleRow = !title && (!!meta || !!action)
  // A titled message with an action is too wide for a phone as one row: the
  // text wraps into a narrow column beside the button. Stack the action under
  // the text there and go back to one row from the sm breakpoint.
  const stacksActionOnMobile = !!title && !!action

  return (
    <div
      className={cn('flex', messageVariants({ variant }), {
        'items-center': isSingleRow,
      })}
    >
      {icon && (
        <div className="items-center space-x-2 py-0.5 [&>svg]:h-4 [&>svg]:w-4">
          {icon}
        </div>
      )}
      <div
        className={cn('flex w-full min-w-0 justify-between gap-x-3', {
          'items-center': isSingleRow,
          'items-start': !isSingleRow,
          'flex-col gap-y-2 sm:flex-row': stacksActionOnMobile,
        })}
      >
        <div className="w-full min-w-0">
          {title && <div className="text-sm font-medium">{title}</div>}
          {children}
        </div>
        {meta && <div className="whitespace-nowrap text-xs">{meta}</div>}
        {action && <div className="flex items-center space-x-3">{action}</div>}
      </div>
    </div>
  )
}
